import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { TrendingUp, DollarSign, Wrench, AlertTriangle, Star } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FLAG_LABELS = {
  water_heater_old: 'Water Heater Old (8+ yrs)',
  no_battery_backup: 'No Battery Backup',
  membership_prospect: 'Membership Prospect',
  softener_replacement: 'Softener Replacement',
  tankless_upgrade: 'Tankless Upgrade',
  financing_prospect: 'Financing Prospect',
  repipe_opportunity: 'Repipe',
  sewer_replacement: 'Sewer Replacement',
};

const OpportunityEngine = () => {
  const [summary, setSummary] = useState({});
  const [filter, setFilter] = useState({ flag: '', asset_type: '', age_over_years: '', no_membership: false });
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadSummary = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/opportunities/summary`, { withCredentials: true });
      setSummary(data);
    } catch { /* */ }
  }, []);

  const runReport = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter.flag) params.flag = filter.flag;
      if (filter.asset_type) params.asset_type = filter.asset_type;
      if (filter.age_over_years) params.age_over_years = filter.age_over_years;
      if (filter.no_membership) params.no_membership = true;
      const { data } = await axios.get(`${API}/admin/opportunities`, { withCredentials: true, params });
      setResults(data.results || []);
    } catch { /* */ }
    setLoading(false);
  }, [filter]);

  useEffect(() => { loadSummary(); }, [loadSummary]);
  useEffect(() => { runReport(); }, [runReport]);

  return (
    <div data-testid="opportunity-engine-page" className="space-y-6">
      <div>
        <h2 className="font-['Outfit'] text-3xl font-bold text-white mb-2">Opportunity Engine</h2>
        <p className="text-[#A3A3A3]">Where Nexflow generates work before the phone rings — aging equipment, missing memberships, flagged service visits.</p>
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Tile testid="opp-water-heater" label="Water Heaters > 8 yrs" value={summary.water_heater_over_8yrs || 0} icon={Wrench} onClick={() => setFilter({ ...filter, flag: '', asset_type: 'water_heater', age_over_years: 8, no_membership: false })} />
        <Tile testid="opp-softener" label="Softeners > 10 yrs" value={summary.softener_over_10yrs || 0} icon={Wrench} onClick={() => setFilter({ ...filter, flag: '', asset_type: 'water_softener', age_over_years: 10, no_membership: false })} />
        {['water_heater_old', 'no_battery_backup', 'membership_prospect'].map((k) => (
          <Tile key={k} testid={`opp-flag-${k}`} label={FLAG_LABELS[k]} value={summary[k] || 0} icon={Star} onClick={() => setFilter({ flag: k, asset_type: '', age_over_years: '', no_membership: false })} />
        ))}
      </div>

      {/* Filter row */}
      <div className="bg-[#141414] border border-[#262626] rounded-md p-3 flex items-center gap-3 flex-wrap" data-testid="opp-filters">
        <Select value={filter.flag || 'any'} onValueChange={(v) => setFilter({ ...filter, flag: v === 'any' ? '' : v })}>
          <SelectTrigger className="w-[220px] bg-[#0A0A0A] border-[#262626] text-white"><SelectValue placeholder="Flag..." /></SelectTrigger>
          <SelectContent className="bg-[#141414] border-[#262626]">
            <SelectItem value="any" className="text-white">Any Flag</SelectItem>
            {Object.entries(FLAG_LABELS).map(([k, l]) => <SelectItem key={k} value={k} className="text-white">{l}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filter.asset_type || 'any'} onValueChange={(v) => setFilter({ ...filter, asset_type: v === 'any' ? '' : v })}>
          <SelectTrigger className="w-[180px] bg-[#0A0A0A] border-[#262626] text-white"><SelectValue placeholder="Asset Type..." /></SelectTrigger>
          <SelectContent className="bg-[#141414] border-[#262626]">
            <SelectItem value="any" className="text-white">Any Type</SelectItem>
            <SelectItem value="water_heater" className="text-white">Water Heater</SelectItem>
            <SelectItem value="water_softener" className="text-white">Softener</SelectItem>
            <SelectItem value="sump_pump" className="text-white">Sump Pump</SelectItem>
            <SelectItem value="tankless_heater" className="text-white">Tankless</SelectItem>
          </SelectContent>
        </Select>
        <Select value={String(filter.age_over_years || '')} onValueChange={(v) => setFilter({ ...filter, age_over_years: v ? Number(v) : '' })}>
          <SelectTrigger className="w-[160px] bg-[#0A0A0A] border-[#262626] text-white"><SelectValue placeholder="Min age..." /></SelectTrigger>
          <SelectContent className="bg-[#141414] border-[#262626]">
            <SelectItem value="any" className="text-white">Any age</SelectItem>
            {[5, 8, 10, 12, 15, 20].map((y) => <SelectItem key={y} value={String(y)} className="text-white">{y}+ years old</SelectItem>)}
          </SelectContent>
        </Select>
        <label className="flex items-center gap-2 text-sm text-white">
          <input type="checkbox" checked={filter.no_membership} onChange={(e) => setFilter({ ...filter, no_membership: e.target.checked })} className="accent-[#D4AF37]" />
          Exclude existing members
        </label>
        <Button data-testid="opp-run" onClick={runReport} className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB] ml-auto">Run Report</Button>
      </div>

      {/* Results */}
      <div className="bg-[#141414] border border-[#262626] rounded-md overflow-hidden">
        <div className="px-4 py-3 border-b border-[#262626] flex items-center justify-between">
          <p className="text-white font-medium">{results.length} matches</p>
          {loading && <p className="text-[#A3A3A3] text-xs">Running...</p>}
        </div>
        <div className="divide-y divide-[#262626] max-h-[600px] overflow-y-auto">
          {results.length === 0 ? (
            <div className="p-10 text-center text-[#A3A3A3]">No opportunities matched. Adjust filters and run again.</div>
          ) : results.map((r, i) => (
            <div key={i} data-testid={`opp-result-${i}`} className="px-4 py-3 hover:bg-[#0A0A0A] flex items-center justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-white font-medium">{r.customer_name || '—'}</p>
                  <span className="text-[10px] tracking-wider uppercase px-2 py-0.5 rounded border border-[#3f3f3f] text-[#A3A3A3]">{r.type.replace('_', ' ')}</span>
                </div>
                <p className="text-[#A3A3A3] text-xs mt-1">
                  {r.flags && r.flags.length > 0 && <span>Flags: {r.flags.map((f) => FLAG_LABELS[f] || f).join(', ')}</span>}
                  {r.asset_type && <span>{r.asset_type.replace('_', ' ')} • {r.brand} {r.model} • Installed {r.install_date || '?'}</span>}
                  {r.type === 'no_membership' && <span>No active membership • Lifetime ${(r.lifetime_spend || 0).toFixed(2)}</span>}
                </p>
              </div>
              {r.customer_id && (
                <a href={`/admin/customers`} className="text-[#D4AF37] text-xs hover:underline" data-testid={`opp-customer-link-${i}`}>Open Customer →</a>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const Tile = ({ icon: Icon, label, value, testid, onClick }) => (
  <button data-testid={testid} onClick={onClick} className="bg-[#141414] border border-[#262626] rounded-md p-4 text-left hover:border-[#D4AF37] transition-colors w-full">
    <div className="flex items-center gap-2 mb-1">
      <Icon className="w-4 h-4 text-[#D4AF37]" />
      <p className="text-[#A3A3A3] text-xs uppercase tracking-wider">{label}</p>
    </div>
    <p className="text-2xl font-bold text-white">{value}</p>
  </button>
);

export default OpportunityEngine;
